import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatQuoteUtcOffset, quoteCivilOf } from './quoteReviewContract';
import type { ParsedWorkReview } from './workReviewPayload';

type Review = Extract<ParsedWorkReview, { kind: 'ownTime' | 'execution' | 'timer' }>;
type Value = string | number | boolean | null;
export function WorkConfirmationReview({ value }: { value: Review }) {
  const { t } = useTranslation('assistant');
  const statuses = new Set(['scheduled', 'pending', 'in_progress', 'paused', 'completed']);
  const status = (value: string) => (statuses.has(value) ? t(`workReview.status.${value}`) : value);
  const display = (v: Value) =>
    v === null
      ? t('confirmation.noValue')
      : typeof v === 'string' && v.trim() === ''
        ? t('confirmation.blankValue')
        : typeof v === 'boolean'
          ? t(v ? 'workReview.yes' : 'workReview.no')
          : String(v);
  const assignment = (id: string | null, caller: boolean) =>
    id === null
      ? null
      : `${t(caller ? 'workReview.assignedToCaller' : 'workReview.assignedToOther')} (${id})`;
  const location = (v: string | null) => (v === 'lift' ? t('workReview.lift') : v);
  const line = (field: string, before: Value, after?: Value) => (
    <Typography component="li" variant="body2" key={field}>
      {t(`workReview.fields.${field}`)}: {display(before)}
      {after !== undefined ? ` → ${display(after)}` : ''}
    </Typography>
  );
  // A continuation instant on the workshop clock with its zone AND the
  // offset in force: the two occurrences of an autumn fall-back hour show
  // the same clock time and differ only there. Without a zone the browser
  // shows the raw instant rather than guess one.
  const workshopTime = (iso: string | null) => {
    const zone = value.kind === 'execution' ? value.timeZone : undefined;
    const civil = iso !== null && zone !== undefined ? quoteCivilOf(iso, zone) : undefined;
    if (!civil || iso === null) return iso;
    const [year, month, day] = civil.date.split('-').map(Number) as [number, number, number];
    const [hour, minute] = civil.time.split(':').map(Number) as [number, number];
    const instant = Date.parse(iso);
    const offset = Date.UTC(year, month - 1, day, hour, minute) - (instant - (instant % 60_000));
    return `${civil.date} ${civil.time} (${zone}, UTC${formatQuoteUtcOffset(offset)})`;
  };
  const target = value.review.command;
  return (
    <Box sx={{ mb: 1.5, overflowWrap: 'anywhere' }} data-testid="work-confirmation-review">
      <Typography variant="body2">
        {t('workReview.target', { workOrderId: target.workOrderId })}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {value.kind === 'timer' ? (
          <>
            {line('entryId', value.review.effects.entryId ?? t('workReview.newEntry'))}
            {line('startedAt', value.review.effects.startedAt)}
            {(['bookingServiceId', 'description', 'breakHours', 'billable', 'laborRateCents'] as const).map(field => line(field, value.review.effects[field]))}
            <Typography component="li" variant="body2">{t(`workReview.clock.${value.review.effects.clock}`)}</Typography>
            <Typography component="li" variant="body2">{t('workReview.clock.duration')}</Typography>
            <Typography component="li" variant="body2">{t('workReview.clock.lifecycle')}</Typography>
          </>
        ) : value.kind === 'ownTime' ? (
          <>
            {line(
              'entryId',
              value.review.command.action === 'create'
                ? t('workReview.newEntry')
                : value.review.command.entryId
            )}
            {(
              [
                'startedAt',
                'endedAt',
                'description',
                'bookingServiceId',
                'durationHours',
                'breakHours',
                'billable',
                'laborRateCents',
              ] as const
            ).map((field) =>
              line(
                field,
                value.review.effects.before?.[field] ?? null,
                value.review.effects.after?.[field] ?? null
              )
            )}
          </>
        ) : (
          <>
            {line(
              'status',
              status(value.review.effects.statusBefore),
              status(value.review.effects.statusAfter)
            )}
            {line(
              'assignedMechanicId',
              assignment(
                value.review.effects.assignedMechanicIdBefore,
                value.review.effects.assignedToCallerBefore
              ),
              assignment(
                value.review.effects.assignedMechanicIdAfter,
                value.review.effects.assignedToCallerAfter
              )
            )}
            {value.review.effects.assignedMechanicIdBefore !== null &&
              value.review.effects.assignedMechanicIdBefore ===
                value.review.effects.assignedMechanicIdAfter && (
                <Typography component="li" variant="body2">
                  {t('workReview.assignmentRetained')}
                </Typography>
              )}
            {value.review.effects.services.map((service) => (
              <Typography component="li" variant="body2" key={service.id}>
                {t('workReview.serviceStatus', {
                  id: service.id,
                  before: status(service.statusBefore),
                  after: status(service.statusAfter),
                })}
              </Typography>
            ))}
            <Box component="li">
              <Typography variant="body2">{t('workReview.callerTimer')}</Typography>
              <Box component="ul" sx={{ pl: 2.5 }}>
                {line('timerEffect', t(`workReview.timer.${value.review.effects.timer.effect}`))}
                {line('entryId', value.review.effects.timer.entryId)}
                {line('bookingServiceId', value.review.effects.timer.bookingServiceId)}
                {line('description', value.review.effects.timer.description)}
                {line('laborRateCents', value.review.effects.timer.laborRateCents)}
              </Box>
            </Box>
            {value.review.effects.otherOpenTimer &&
              line('otherOpenTimer', value.review.effects.otherOpenTimer.entryId)}
            {line(
              'pauseLocation',
              location(value.review.effects.pauseLocationBefore),
              location(value.review.effects.pauseLocationAfter)
            )}
            {line(
              'pauseReason',
              value.review.effects.pauseReasonBefore,
              value.review.effects.pauseReasonAfter
            )}
            {line(
              'expectedResumeAt',
              workshopTime(value.review.effects.expectedResumeAtBefore),
              workshopTime(value.review.effects.expectedResumeAtAfter)
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
